import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCwIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  StatTile,
  TrendChart,
  type TrendBand,
  type TrendChartVariant,
  type TrendOverlay,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  CHART_VARIANTS,
  INTERVAL_LABELS,
  LIVE_REFRESH_MS,
  RANGES,
  TREND_WINDOWS,
  agoLabel,
  money,
  periodLabel,
  rangeWindow,
  toTrendOverlays,
  toTrendPoints,
  toTrendSeries,
  toYoyOverlay,
  type RangeKey,
  type TrendWindowKey,
} from '@/lib/report-window';
import { BreakdownPanel, TogglePill } from '@/components/report-bits';
import { RevenueMathPanel } from '@/components/RevenueMathPanel';
import { LeadPacingPanel } from '@/components/LeadPacingPanel';
import { LeadCohortsSection } from '@/components/LeadCohortsSection';
import { LeadTimingPanel } from '@/components/LeadTimingPanel';
import { LeadsWithoutLeadPanel } from '@/components/LeadsWithoutLeadPanel';
import { useLocalSort } from '@/lib/use-local-sort';
import { filterRows } from '@/lib/facet-table';
import {
  readLeadStats,
  readLeadSyncStatus,
  STATS_INTERVAL,
  syncLeadsNow,
  STATS_SEGMENT,
  type LeadBreakdownRow,
  type StatsInterval,
  type StatsSegment,
} from '@/lib/leads';
import { useIntervalChoice } from '@/lib/use-interval-choice';
import { buildSignedView } from '@/lib/leads-signed-view';
import { LeadsListSection } from './leads-list-section';

/** Split choices drawn on the page from the totals, never sent to the API as a segment. */
const LOCAL_SPLIT = { none: 'none', signed: 'signed' } as const;
type LocalSplit = (typeof LOCAL_SPLIT)[keyof typeof LOCAL_SPLIT];
type SegmentChoice = StatsSegment | LocalSplit;
const SEGMENT_OPTIONS: readonly { key: SegmentChoice; label: string }[] = [
  { key: LOCAL_SPLIT.none, label: 'Total' },
  { key: LOCAL_SPLIT.signed, label: 'Signed' },
  { key: STATS_SEGMENT.channel, label: 'By channel' },
  { key: STATS_SEGMENT.form, label: 'By form' },
  { key: STATS_SEGMENT.owner, label: 'By owner' },
];

/**
 * Aug 2025 to Jun 2026: the Gravity Forms migration stopped the Google Ads
 * click id (GCLID) reaching Salesforce, so paid leads from those months read
 * as "Website form". Not recoverable per lead (hq ppc-seo README), so the
 * channel view shades the range instead of letting it read as a real shift.
 */
const GCLID_CAPTURE_GAP: TrendBand = { from: '2025-08-01', to: '2026-06-30', label: 'Click ID capture broken' };

/** Opportunities reps created with no lead: a line beside the bars, never part of them. */
const WITHOUT_LEAD_LABEL = 'Opportunities without a lead';
/** The fourth overlay stroke (dash-dot brown), clear of the trend averages and last year. */
const WITHOUT_LEAD_TONE = 3;
const CLOSE_RATE_LABEL = 'Close rate';
const PERCENT_AXIS = { formatValue: (value: number) => `${value.toLocaleString()}%`, max: 100 } as const;

function isLocalSplit(choice: SegmentChoice): choice is LocalSplit {
  return choice === LOCAL_SPLIT.none || choice === LOCAL_SPLIT.signed;
}

// Re-exported for the page tests, which exercise the window math and the channel label from here.
export { rangeWindow } from '@/lib/report-window';
export { leadChannel } from './leads-list-section';

/**
 * Lead analytics (admin-only): how many leads are arriving, from where, and
 * how that is trending — over a mirror of Salesforce Leads refreshed every
 * five minutes. The API enforces the role; the nav hides the page otherwise.
 *
 * @returns The leads page.
 */
export function LeadsPage(): React.ReactElement {
  const [range, setRange] = useState<RangeKey>('30d');
  const [variant, setVariant] = useState<TrendChartVariant>('bar');
  const [trends, setTrends] = useState<ReadonlySet<TrendWindowKey>>(() => new Set(['short']));
  const [segment, setSegment] = useState<SegmentChoice>(LOCAL_SPLIT.none);
  const [yoy, setYoy] = useState(false);
  const [includeUnqualified, setIncludeUnqualified] = useState(false);
  const [showWithoutLead, setShowWithoutLead] = useState(true);
  const [showCloseRate, setShowCloseRate] = useState(false);
  const signedSplit = segment === LOCAL_SPLIT.signed;
  const apiSegment = isLocalSplit(segment) ? undefined : segment;
  const statsWindow = useMemo(() => rangeWindow(range), [range]);
  const [effectiveInterval, setIntervalChoice] = useIntervalChoice(RANGES[range].interval);
  // Enough history before `from` for the longest average to be full from day one.
  const leadIn = Math.max(...TREND_WINDOWS[effectiveInterval].map((window) => window.buckets)) - 1;
  const queryClient = useQueryClient();

  const stats = useQuery({
    queryKey: ['lead-stats', statsWindow.from, statsWindow.to, effectiveInterval, leadIn, apiSegment, yoy, includeUnqualified],
    queryFn: ({ signal }) =>
      readLeadStats(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          interval: effectiveInterval,
          leadIn,
          ...(apiSegment !== undefined ? { segment: apiSegment } : {}),
          ...(yoy ? { yoy: '1' as const } : {}),
          ...(includeUnqualified ? { unqualified: '1' as const } : {}),
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: (previous) => previous,
  });
  const sync = useQuery({
    queryKey: ['lead-sync-status'],
    queryFn: ({ signal }) => readLeadSyncStatus(signal),
    refetchInterval: LIVE_REFRESH_MS,
  });
  const syncNow = useMutation({
    mutationFn: syncLeadsNow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-sync-status'] });
    },
  });

  if (stats.isPending) return <LoadingState label="Loading leads…" />;
  if (stats.isError) {
    return <ErrorNotice title="Leads unavailable" message={errorMessage(stats.error)} />;
  }

  const data = stats.data;
  const totals = data.totals;
  const signedRate = totals.leads === 0 ? 0 : Math.round((totals.signed / totals.leads) * 1000) / 10;
  const valuePerLead = totals.leads === 0 ? 0 : totals.signedValue / totals.leads;
  const paidShare = totals.leads === 0 ? 0 : Math.round((totals.paid / totals.leads) * 100);
  const withoutLead = data.withoutLead;
  // Per bucket, for the tooltip: the series carries the lead-in buckets too.
  const withoutLeadByPeriod = new Map(data.series.map((point, index) => [point.period, withoutLead?.series[index] ?? 0]));
  const withoutLeadOverlay: TrendOverlay[] =
    withoutLead && showWithoutLead && !signedSplit
      ? [{ key: 'without-lead', label: WITHOUT_LEAD_LABEL, values: withoutLead.series.slice(data.range.leadIn), tone: WITHOUT_LEAD_TONE }]
      : [];
  const signedView = signedSplit ? buildSignedView(data, { showWithoutLead, showCloseRate }) : null;
  const lastRun = sync.data?.lastRun ?? null;
  const syncHealthy = lastRun !== null && lastRun.error === null;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Leads"
        subtitle="Every Salesforce lead, mirrored into BCEcloud every five minutes — how many are arriving, from where, and how that is trending."
      />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <SegmentedControl<RangeKey>
            label="Range"
            value={range}
            onChange={(next) => {
              setRange(next);
            }}
            options={Object.values(RANGES).map((preset) => ({ key: preset.key, label: preset.label }))}
          />
          <SegmentedControl<StatsInterval>
            label="Interval"
            value={effectiveInterval}
            onChange={setIntervalChoice}
            options={(Object.keys(INTERVAL_LABELS) as StatsInterval[]).map((key) => ({
              key,
              label: INTERVAL_LABELS[key],
            }))}
          />
          <div className="flex items-end">
            <TogglePill on={includeUnqualified} onToggle={() => setIncludeUnqualified((previous) => !previous)}>
              Include not counted
            </TogglePill>
          </div>
        </div>
        <div className="flex items-center gap-3 text-portal-micro text-portal-muted">
          {sync.data ? (
            <span className="flex items-center gap-2">
              <Pill tone={syncHealthy ? 'success' : lastRun === null ? 'neutral' : 'danger'}>
                {syncHealthy ? 'Live' : lastRun === null ? 'Not synced yet' : 'Sync failing'}
              </Pill>
              <span>
                {sync.data.leads.toLocaleString()} leads · synced {agoLabel(lastRun?.finishedAt ?? null)}
              </span>
            </span>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending}
            aria-label="Sync leads from Salesforce now"
          >
            <RefreshCwIcon className={syncNow.isPending ? 'size-4 animate-spin' : 'size-4'} aria-hidden="true" />
            {syncNow.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
        </div>
      </div>
      {syncNow.isError ? (
        <div className="mb-4">
          <ErrorNotice title="Sync failed" message={errorMessage(syncNow.error)} />
        </div>
      ) : null}
      {lastRun?.error ? (
        <p className="mb-4 text-portal-micro text-rose-700">Last sync error: {lastRun.error}</p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatTile
          label="Leads"
          value={totals.leads.toLocaleString()}
          changePct={totals.changePct}
          hint={`vs ${totals.previousLeads.toLocaleString()} the previous ${RANGES[range].label}`}
        />
        <StatTile
          label="Leads per day"
          value={totals.perDay.toLocaleString()}
          hint={`vs ${(Math.round((totals.previousLeads / RANGES[range].days) * 10) / 10).toLocaleString()}/day the previous ${RANGES[range].label}`}
        />
        <StatTile
          label="Signed"
          value={`${signedRate}%`}
          hint={`${totals.signed.toLocaleString()} signed a contract · ${money.format(totals.signedValue)}`}
        />
        <StatTile
          label="Value per lead"
          value={money.format(valuePerLead)}
          hint={totals.signed > 0 ? `${money.format(totals.signedValue / totals.signed)} per signed contract` : 'No signed contracts yet'}
        />
        <StatTile
          label="Still working"
          value={totals.open.toLocaleString()}
          hint={[
            `${totals.lost.toLocaleString()} lost`,
            ...(includeUnqualified ? [`${totals.unqualified.toLocaleString()} unqualified`] : []),
            `${totals.none.toLocaleString()} no opportunity`,
          ].join(' · ')}
        />
        <StatTile
          label="Paid search"
          value={`${paidShare}%`}
          hint={`${totals.paid.toLocaleString()} arrived with a Google Ads click id`}
        />
      </div>

      <Panel className="mt-4 p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-portal-card-title font-bold text-portal-ink">Leads over time</h2>
            <p className="text-portal-micro text-portal-muted">
              {INTERVAL_LABELS[effectiveInterval]} · {periodLabel(data.range.from, STATS_INTERVAL.day)} –{' '}
              {periodLabel(data.range.to, STATS_INTERVAL.day)} (Eastern days)
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl<SegmentChoice>
              label="Split by"
              value={segment}
              onChange={setSegment}
              options={SEGMENT_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
            />
            {signedSplit ? null : (
              <SegmentedControl<TrendChartVariant>
                label="Chart type"
                value={variant}
                onChange={setVariant}
                options={CHART_VARIANTS.map((option) => ({ key: option.key, label: option.label }))}
              />
            )}
            <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Trend lines">
              {TREND_WINDOWS[effectiveInterval].map((window) => (
                <TogglePill
                  key={window.key}
                  on={trends.has(window.key)}
                  onToggle={() =>
                    setTrends((previous) => {
                      const next = new Set(previous);
                      if (next.has(window.key)) next.delete(window.key);
                      else next.add(window.key);
                      return next;
                    })
                  }
                >
                  {window.label}
                </TogglePill>
              ))}
              <TogglePill on={yoy} onToggle={() => setYoy((previous) => !previous)}>
                Last year
              </TogglePill>
              {withoutLead ? (
                <TogglePill on={showWithoutLead} onToggle={() => setShowWithoutLead((previous) => !previous)}>
                  {WITHOUT_LEAD_LABEL}
                </TogglePill>
              ) : null}
              {signedSplit ? (
                <TogglePill on={showCloseRate} onToggle={() => setShowCloseRate((previous) => !previous)}>
                  {CLOSE_RATE_LABEL}
                </TogglePill>
              ) : null}
            </div>
          </div>
        </div>
        {totals.leads === 0 ? (
          <EmptyState title="No leads in this range" message="Try a wider range." />
        ) : signedView ? (
          <>
            <TrendChart
              points={toTrendPoints(data, (point) => point.leads).map((point, index) => ({
                ...point,
                details: signedView.details[index] ?? [],
              }))}
              series={signedView.series}
              overlays={[
                ...toTrendOverlays(data, data.series.map((point) => point.leads), trends),
                ...(toYoyOverlay(data) ? [toYoyOverlay(data)!] : []),
                ...signedView.overlays,
              ]}
              {...(showCloseRate ? { secondaryAxis: PERCENT_AXIS } : {})}
              variant="bar"
              title={signedView.withoutLeadShown ? 'Leads and opportunities without a lead, signed and not signed' : 'Leads, signed and not signed'}
              unit="leads"
            />
            <p className="mt-2 text-portal-micro text-portal-muted">
              Each period's bars count what was created in it and how much of that has signed so far
              {showCloseRate ? '; close rate (right axis) is signed ÷ created, so recent periods are still closing' : ''}.
              {signedView.withoutLeadShown ? ' Opportunities without a lead are never in the lead count.' : ''}
            </p>
          </>
        ) : (
          <TrendChart
            points={toTrendPoints(data, (point) => point.leads, (point) => [
              ...(point.leads > 0 ? [`${point.signed} signed`] : []),
              ...(withoutLead && showWithoutLead ? [`${withoutLeadByPeriod.get(point.period) ?? 0} opportunities without a lead (not counted)`] : []),
            ])}
            series={toTrendSeries(data)}
            overlays={[
              ...toTrendOverlays(data, data.series.map((point) => point.leads), trends),
              ...(toYoyOverlay(data) ? [toYoyOverlay(data)!] : []),
              ...withoutLeadOverlay,
            ]}
            variant={variant}
            {...(segment === STATS_SEGMENT.channel ? { bands: [GCLID_CAPTURE_GAP] } : {})}
            title="Leads"
            unit="leads"
          />
        )}
      </Panel>

      {withoutLead ? <LeadsWithoutLeadPanel stats={withoutLead} previousLabel={`the previous ${RANGES[range].label}`} /> : null}

      <LeadPacingPanel />

      <LeadTimingPanel />

      <div className="mt-8">
        <LeadCohortsSection interval={effectiveInterval} from={statsWindow.from} />
      </div>

      <Panel className="mt-4">
        <div className="px-5 pt-5 pb-4">
          <h2 className="text-portal-card-title font-bold text-portal-ink">What a lead is worth, by channel</h2>
          <p className="text-portal-micro text-portal-muted">
            Leads created in the range → contracts signed so far (recent leads have had less time to close).
          </p>
        </div>
        <ChannelValueTable rows={data.byChannel} />
      </Panel>

      <RevenueMathPanel />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <BreakdownPanel
          title="By channel"
          hint="Derived: Google Ads click id, then Lead Source, then website form, then hand-entered"
          rows={data.byChannel}
          total={totals.leads}
        />
        <BreakdownPanel title="By Lead Source (raw)" rows={data.bySource} total={totals.leads} />
        <BreakdownPanel title="By website form" rows={data.byForm} total={totals.leads} />
        <BreakdownPanel title="By owner" rows={data.byOwner} total={totals.leads} />
      </div>

      <LeadsListSection window={statsWindow} />
    </section>
  );
}

/** Sort accessors for the channel-value table — module-level so the hook memo holds. */
const CHANNEL_VALUE_ACCESSORS: Readonly<Record<string, (row: LeadBreakdownRow) => number | string | null>> = {
  key: (row) => row.key,
  leads: (row) => row.leads,
  signed: (row) => row.signed,
  signedRate: (row) => (row.leads === 0 ? 0 : row.signed / row.leads),
  value: (row) => row.value,
  perLead: (row) => (row.leads === 0 ? 0 : row.value / row.leads),
  avgContract: (row) => (row.signed === 0 ? null : row.value / row.signed),
};

/** The channel fields the search box matches. */
const CHANNEL_VALUE_SEARCH_FIELDS: ReadonlyArray<keyof LeadBreakdownRow> = ['key'];

/** Signed leads as a share of all leads, one decimal ("12.9%"); a dash with no leads. */
function signedRateLabel(row: LeadBreakdownRow): string {
  return row.leads === 0 ? '—' : `${(Math.round((row.signed / row.leads) * 1000) / 10).toLocaleString()}%`;
}

/** Booked value spread over every lead in the channel. */
function valuePerLeadLabel(row: LeadBreakdownRow): string {
  return row.leads === 0 ? '—' : money.format(row.value / row.leads);
}

/** Booked value per signed contract. */
function avgContractLabel(row: LeadBreakdownRow): string {
  return row.signed === 0 ? '—' : money.format(row.value / row.signed);
}

/**
 * Leads → signed contracts → value, per channel. Its search is local state:
 * `?q=` already belongs to the leads list on the same page.
 */
function ChannelValueTable({ rows }: { readonly rows: readonly LeadBreakdownRow[] }): React.ReactElement {
  const [search, setSearch] = useState('');
  const { sorted, sort, onSort } = useLocalSort(rows, CHANNEL_VALUE_ACCESSORS, {
    key: 'value',
    direction: 'desc',
  });
  if (rows.length === 0) {
    return <p className="px-5 pb-5 text-portal-micro text-portal-muted">Nothing in this range.</p>;
  }
  const shown = filterRows(sorted, search, CHANNEL_VALUE_SEARCH_FIELDS);
  return (
    <>
    <div className="px-5 pb-4">
      <SearchInput
        label="Search channels"
        placeholder="Channel"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="w-full max-w-sm"
      />
    </div>
    {shown.length === 0 ? (
      <div className="px-5 pb-5">
        <EmptyState title="No channels match" message="Try a different search." />
      </div>
    ) : (
    <DataTable
      headers={[
        { label: 'Channel', sortKey: 'key' },
        { label: 'Leads', sortKey: 'leads' },
        { label: 'Signed', sortKey: 'signed' },
        { label: 'Signed rate', sortKey: 'signedRate' },
        { label: 'Booked value', sortKey: 'value' },
        { label: 'Value per lead', sortKey: 'perLead' },
        { label: 'Avg contract', sortKey: 'avgContract' },
      ]}
      rightAlignedHeaders={[1, 2, 3, 4, 5, 6]}
      minWidth={760}
      flush
      sort={sort}
      onSort={onSort}
      cards={shown.map((row) => ({
        key: row.key,
        title: row.key,
        fields: [
          { label: 'Value per lead', value: valuePerLeadLabel(row) },
          { label: 'Booked value', value: money.format(row.value) },
          { label: 'Leads', value: row.leads.toLocaleString() },
          { label: 'Signed', value: `${row.signed.toLocaleString()} (${signedRateLabel(row)})` },
          { label: 'Avg contract', value: avgContractLabel(row) },
        ],
      }))}
    >
      {shown.map((row) => (
        <tr key={row.key} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small font-bold text-portal-ink">{row.key}</td>
          <td className="p-3 text-right text-portal-small tabular-nums">{row.leads.toLocaleString()}</td>
          <td className="p-3 text-right text-portal-small tabular-nums">{row.signed.toLocaleString()}</td>
          <td className="p-3 text-right text-portal-small tabular-nums">{signedRateLabel(row)}</td>
          <td className="p-3 text-right text-portal-small tabular-nums">{money.format(row.value)}</td>
          <td className="p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">{valuePerLeadLabel(row)}</td>
          <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{avgContractLabel(row)}</td>
        </tr>
      ))}
    </DataTable>
    )}
    </>
  );
}
